import React, { useMemo, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { Link } from "react-router";
import { Container, Spinner } from "reactstrap";
import { Button } from "react-bootstrap";
import { Stack } from "react-bootstrap";
import { useForm, FormProvider, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { PageHeader } from "@/CommonElements/PageTitle";
import { Controllers } from "@/CommonElements/Controller";
import { ActionsFooter } from "@/Component/ActionsFooter";
import { useCreateFlightDetailMutation, useUpdateFlightDetailMutation, useGetFlightDetailsByIdQuery } from "@/services/reports";
import { useFlightTerminalsQuery } from "@/services/basicData";
import { useLocalizedText } from "@/utils/hooks/useLocalizedText";
import { getFlightFormFields, fieldConfigToController } from "./flightFormConfig";
import { FlightEditProvider } from "./FlightEditContext";
import { cn, handleFormErrors } from "@/utils/helpers";
import { useSystem } from "@/utils/hooks/useLocation";

const FORM_LAYOUT = { colProps: { xs: 6, md: 6 } };

/** Map API row (snake_case / nested objects) → form default values (snake_case keys) */
const buildPayload = (row) => {
  if (!row) return {};
  return {
    flight_no: row.flight_no ?? null,
    direction: row.direction?.code ?? row.direction ?? null,
    flight_date: row.flight_date ? dayjs(row.flight_date) : null,
    airline_id: row.airline?.id != null ? String(row.airline.id) : null,
    airport_code: row.airport_code ?? null,
    terminal_code: row.terminal?.code ?? row.terminal_code ?? null,
    from_city_id: row.from_city?.id ?? row.from_city_id ?? null,
    to_city_id: row.to_city?.id ?? row.to_city_id ?? null,
    max_capacity: row.max_capacity ?? null,
  };
};

const normalizeFlightSubmitPayload = (data) => {
  if (!data) return data;

  const normalized = { ...data };

  // Keep selected local date-time as entered (avoid UTC ISO shift).
  if (normalized.flight_date) {
    normalized.flight_date = dayjs(normalized.flight_date).format("YYYY-MM-DD HH:mm:ss");
  }

  return normalized;
};


/**
 * Single page for both Create and Edit flight.
 * Mode is determined by the route URL: .../create vs .../edit/:id
 */
export const CreateFlight = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const { currentSystem } = useSystem();
  const row = location.state?.row;

  const isEditMode = Boolean(id);
  const shouldFetchById = isEditMode && !row;

  const { data: flightByIdRes, isLoading: isFlightByIdLoading, isError: isFlightByIdError } = useGetFlightDetailsByIdQuery(
    id,
    { enabled: shouldFetchById }
  );
  const fetchedRow = flightByIdRes?.data?.data ?? null;


  const form = useForm();
  const { handleSubmit, reset } = form;

  useEffect(() => {
    if (isEditMode) reset(buildPayload(row || fetchedRow));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchedRow, isEditMode, row]);



  const { mutate: createFlight, isPending: isCreatePending } = useCreateFlightDetailMutation();
  const { mutate: updateFlight, isPending: isUpdatePending } = useUpdateFlightDetailMutation();
  const isPending = isCreatePending || isUpdatePending;

  const onSubmit = (data) => {
      const payload = normalizeFlightSubmitPayload(data);

      if (isEditMode && id) {
        updateFlight(
          {
            id: Number(id),
            ...payload,
          },
          { onSuccess: () => navigate(`/app/${currentSystem}/flights`) }
        );
      } else {
        createFlight(
          payload,
          {
            onSuccess: () => navigate(`/app/${currentSystem}/flights`),
            onError: (error) => {
              handleFormErrors(error, form.setError, t, "CREATE_FLIGHT_FAILED");
            },
          }
        );
      }
    }

  const pageTitle = isEditMode ? t("EDIT_FLIGHT") : t("CREATE_FLIGHT");
  const submitLabel = isEditMode ? t("UPDATE") : t("CREATE");
  
  useEffect(() => {
    return () => {
      reset();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isEditMode && !row && !fetchedRow) {
    if (isFlightByIdLoading) {
      return (
        <Stack gap={2}>
          <PageHeader title={pageTitle} />
          <Container fluid className="d-flex justify-content-center py-5">
            <Spinner />
          </Container>
        </Stack>
      );
    }
    if (isFlightByIdError || !isFlightByIdLoading) {
      return (
        <Stack gap={2}>
          <PageHeader title={pageTitle} />
          <Container fluid>
            <p className="text-muted">{t("FLIGHT_NOT_FOUND") || "Flight not found. Go back to the list."}</p>
            <Link to={`/app/${currentSystem}/flights`} className="btn btn-primary">
              {t("BACK_TO_FLIGHTS") || "Back to flights"}
            </Link>
          </Container>
        </Stack>
      );
    }
  }

  return (
    <Stack gap={2}>
      <PageHeader title={pageTitle} backTo={`/app/${currentSystem}/flights`} />
      <Container fluid>
        <FormProvider {...form}>
          <FlightEditProvider value={row ?? fetchedRow ?? null}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <FlightForm />
            <ActionsFooter>
              <Button className="btn btn-primary" type="submit" disabled={isPending}>
                {isPending ? <Spinner size="sm" /> : null}
                {submitLabel}
              </Button>
              <Link
                disabled={isPending}
                style={{ pointerEvents: isPending ? "none" : "auto" }}
                to={`/app/${currentSystem}/flights`}
                className="btn btn-danger"
              >
                {t("CANCEL")}
              </Link>
            </ActionsFooter>
            </form>
          </FlightEditProvider>
        </FormProvider>
      </Container>
    </Stack>
  );
};

const FlightForm = () => {
  const form = useFormContext();
  const { t } = useTranslation();
  const { getLocalizedText } = useLocalizedText();
  const { data: terminalsRes, isLoading: isTerminalsLoading } = useFlightTerminalsQuery();
  const { control, formState: { errors } } = form;

  const flightFormFields = useMemo(
    () => getFlightFormFields(t, getLocalizedText, terminalsRes?.data?.data ?? []),
    [t, getLocalizedText, terminalsRes?.data?.data]
  );
  const createFormFields = useMemo(
    () => flightFormFields.filter((f) => !f.filterOnly),
    [flightFormFields]
  );
  const controllers = useMemo(
    () =>
      createFormFields.map((fieldConfig) =>
        fieldConfigToController(fieldConfig, { control, errors, t, getLocalizedText })
      ),
    [createFormFields, control, errors, t, getLocalizedText]
  );
  const controllersWithLayout = useMemo(
    () =>
      controllers.map((config) => ({
        ...config,
        labelProps: { ...config.labelProps, className: cn("fw-bold", config.labelProps?.className) },
        controllerProps: { ...config.controllerProps, control, errors },
        layout: FORM_LAYOUT,
      })),
    [controllers, control, errors]
  );

  return (
    <Controllers
      isLoading={isTerminalsLoading}
      watch={form.watch}
      controllers={controllersWithLayout}
    />
  );
};

export default CreateFlight;
